(function idleQueue(global) {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};
  global.NewHomeSource.Modules = global.NewHomeSource.Modules || {};

  if (!global.NewHomeSource.Modules.requestIdleCallback) {
    return;
  }

  const previousIdleQueue = global.NewHomeSource.idleQueue;
  const isSafari = !!(typeof global.safari === 'object' && global.safari.pushNotification);
  const minTaskTime = 10;
  const requestIdleCallbackFunction = global.NewHomeSource.Modules.requestIdleCallback.rIC;
  const cancelIdleCallbackFunction = global.NewHomeSource.Modules.requestIdleCallback.cIC;

  /**
   * @private
   * shouldYield: true if no deadline is defined or if the timeRemaining
   * is not greater than the minimumTaskTime, otherwise false.
   *
   * @param {number} deadline reamining time available from the request idle callback.
   * @param {number} minimumTaskTime minimum ramianing time available to execute the task.
   */
  function shouldYield(deadline, minimumTaskTime) {
    if (deadline <= minimumTaskTime) {
      return true;
    }

    return false;
  }

  /**
   * @private
   * scheduleTasksToRun: schedules the task queue to be processed.
   *
   * @param {boolean} retry true if the task should be enqueue in case of error, otherwise false.
   */
  function scheduleTasksToRun(retry) {
    if (this.idleCallbackHandle) {
      return;
    }

    // eslint-disable-next-line no-use-before-define
    this.idleCallbackHandle = requestIdleCallbackFunction(runTasks.bind(this, retry));
  }

  /**
   * @private
   * runTasks: runs as many tasks in the queue as it can before reaching the deadline.
   * If no deadline is pass, it will run all tasks.
   *
   * @param {boolean} retry true if the task should be enqueue in case of error, otherwise false.
   * @param {IdleDeadline} deadline input parameter to idle callbacks.
   */
  function runTasks(retry, deadline) {
    // Do not start processing the pending tasks until the DOM has reached the ready state.
    const isDomCompleted = global.document.readyState === 'complete';
    this.cancelScheduledRun();

    if (this.isProcessing) {
      return;
    }

    this.isProcessing = true;

    while (this.hasPendingTasks() && isDomCompleted) {
      const remainingTime = deadline ? deadline.timeRemaining() : 50;

      if (shouldYield(remainingTime, this.taskQueue[0].minTaskTime)) {
        break;
      }

      const taskToRun = this.taskQueue.shift();
      const isExecuted = taskToRun.task(taskToRun.state);
      const timeLapsed = +new Date() - taskToRun.state.time;

      if (!isExecuted && retry && timeLapsed < 15000) {
        this.pushTask(taskToRun.task, taskToRun.state.time);
      }
    }

    if (this.hasPendingTasks()) {
      if (isDomCompleted) {
        scheduleTasksToRun.call(this, retry);
      } else {
        window.addEventListener('load', scheduleTasksToRun.bind(this, retry));
      }
    }

    this.isProcessing = false;
  }

  /**
   * @public
   * onVisibilityChange: exectutes the pending tasks in a sync way when the page is hidden.
   */
  function onVisibilityChange() {
    if (document.visibilityState !== 'hidden') {
      return;
    }

    this.runTasksImmediately();
  }

  /**
   * @constructs NewHomeSource.Modules.IdleQueue
   */
  function IdleQueue(name) {
    this.idleCallbackHandle = null;
    this.taskQueue = [];
    this.isProcessing = false;
    this.id = name;

    if (isSafari) {
      global.addEventListener('beforeunload', IdleQueue.prototype.runTasksImmediately.bind(this), true);
    }

    global.addEventListener('visibilitychange', onVisibilityChange.bind(this), true);
  }

  /**
   * @public
   * cancelScheduledRun: cancels any scheduled idle callback and removes the handler.
   */
  IdleQueue.prototype.cancelScheduledRun = function cancelScheduledRun() {
    cancelIdleCallbackFunction(this.idleCallbackHandle);
    this.idleCallbackHandle = null;
  };

  /**
   * @public
   * hasPendingTasks: true if the queue still has tasks pending to run, otherwise false.
   */
  IdleQueue.prototype.hasPendingTasks = function hasPendingTasks() {
    return this.taskQueue.length > 0;
  };

  /**
   * @public
   *
   * pushTask: adds a new task to the idleQueue to be executed.
   * @param {function} task to be executed.
   * @param {number} time when the task was added to the queue.
   */
  IdleQueue.prototype.pushTask = function pushTask(task, time) {
    if (typeof task !== 'function') {
      return;
    }

    const taskToRun = {
      id: this.id,
      minTaskTime,
      task,
      state: {
        time: time || +new Date(),
        visibilityState: global.document.visibilityState
      }
    };

    this.taskQueue.push(taskToRun);
    scheduleTasksToRun.call(this, true);
  };

  /**
   * @public
   * runTasksImmediately: runs all the pending tasks in sync way.
   */
  IdleQueue.prototype.runTasksImmediately = function runTasksImmediately() {
    runTasks.call(this, false);
  };

  /**
   * @public
   *
   * noConflict: Returns control of NewHomeSource.Modules.IdleQueue to the previous script.
   */
  IdleQueue.noConflict = function noConflict() {
    global.NewHomeSource.Modules.IdleQueue = previousIdleQueue;
    return IdleQueue;
  };

  global.NewHomeSource.Modules.IdleQueue = IdleQueue;
}(window));
